/**
 * TransferToSacDialog — used by hostel_admin / school_admin from a student's
 * row. POSTs to /api/sac/transfer/{student_id}. If the server replies
 * `{status:'incomplete', missing_fields:[...]}` we show those fields inline
 * with a guidance message; otherwise we toast success and refresh.
 *
 * Composition:
 *   - useSacTransferForm : owns the form state + submit logic
 *   - MissingFieldsBlock : shown when server reports an incomplete profile
 *   - SacProfileFormFields : the actual input grid
 */
import React, { useCallback, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { HeartHalf, ListChecks } from "@phosphor-icons/react";
import { toast } from "sonner";

function initialProfile(student) {
  return {
    father_name: student?.father_name || "",
    mother_name: student?.mother_name || "",
    state: student?.state || "",
    district: student?.district || "",
    city: student?.city || "",
    pincode: student?.pincode || "",
    full_address: student?.full_address || "",
    pct_obtained_current: "",
    pct_obtained_previous: "",
    favourite_subject: "",
    special_talent: "",
    any_achievement: "",
    what_child_wants_to_be: "",
  };
}

function useSacTransferForm(student, onSuccess, onClose) {
  const [profile, setProfile] = useState(() => initialProfile(student));
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(null);

  const setField = useCallback((k, v) => setProfile((p) => ({ ...p, [k]: v })), []);

  const submit = useCallback(async () => {
    setBusy(true);
    setMissing(null);
    try {
      const cleaned = Object.fromEntries(
        Object.entries(profile).filter(([, v]) => v !== "")
      );
      if (cleaned.pct_obtained_current) cleaned.pct_obtained_current = Number(cleaned.pct_obtained_current);
      if (cleaned.pct_obtained_previous) cleaned.pct_obtained_previous = Number(cleaned.pct_obtained_previous);
      const res = await api.post(`/sac/transfer/${student.id}`, { profile: cleaned });
      if (res.data?.ok) {
        toast.success("Transferred to Support a Child");
        onClose();
        onSuccess?.();
      } else if (res.data?.status === "incomplete") {
        setMissing(res.data.missing_fields);
        toast.error("Some required information is missing");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }, [profile, student, onSuccess, onClose]);

  return { profile, setField, busy, missing, submit };
}

function MissingFieldsBlock({ missing }) {
  if (!missing) return null;
  return (
    <div className="vs-card p-4 mb-3 border-[var(--vs-danger)]/40 bg-[var(--vs-danger)]/8" data-testid="sac-missing-fields">
      <div className="flex items-center gap-2 mb-2 font-medium text-[var(--vs-danger)]">
        <ListChecks size={14} weight="duotone" /> Still missing
      </div>
      <ul className="text-sm list-disc pl-5 space-y-0.5">
        {missing.map((m) => <li key={m}>{m}</li>)}
      </ul>
      <p className="text-xs text-[var(--vs-text-secondary)] mt-2">
        Fill these in below, or attach the photo / marksheet / activity reports on the student record first, then try again. Super-admin will see this in their "Incomplete SAC profiles" list.
      </p>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function SacProfileFormFields({ profile, setField }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Field label="Father's name" value={profile.father_name} onChange={(v) => setField("father_name", v)} testid="sac-father" />
      <Field label="Mother's name" value={profile.mother_name} onChange={(v) => setField("mother_name", v)} testid="sac-mother" />
      <Field label="State" value={profile.state} onChange={(v) => setField("state", v)} testid="sac-state" />
      <Field label="District" value={profile.district} onChange={(v) => setField("district", v)} testid="sac-district" />
      <Field label="City / Town" value={profile.city} onChange={(v) => setField("city", v)} testid="sac-city" />
      <Field label="Pincode" value={profile.pincode} onChange={(v) => setField("pincode", v)} testid="sac-pincode" />
      <div className="sm:col-span-2">
        <Label>Full address</Label>
        <Textarea data-testid="sac-full-address" rows={2} value={profile.full_address} onChange={(e) => setField("full_address", e.target.value)} />
      </div>
      <Field label="% obtained (current year)" type="number" value={profile.pct_obtained_current} onChange={(v) => setField("pct_obtained_current", v)} testid="sac-pct-current" />
      <Field label="% obtained (previous year)" type="number" value={profile.pct_obtained_previous} onChange={(v) => setField("pct_obtained_previous", v)} testid="sac-pct-previous" />
      <Field label="Favourite subject" value={profile.favourite_subject} onChange={(v) => setField("favourite_subject", v)} testid="sac-fav-subject" />
      <Field label="Special talent / interest" value={profile.special_talent} onChange={(v) => setField("special_talent", v)} testid="sac-talent" />
      <Field label="Any achievement" value={profile.any_achievement} onChange={(v) => setField("any_achievement", v)} testid="sac-achievement" />
      <Field label="What does the child want to be?" value={profile.what_child_wants_to_be} onChange={(v) => setField("what_child_wants_to_be", v)} testid="sac-aspiration" />
    </div>
  );
}

export function TransferToSacButton({ student, onSuccess, label = "Transfer to SAC" }) {
  const [open, setOpen] = useState(false);
  const { profile, setField, busy, missing, submit } = useSacTransferForm(student, onSuccess, () => setOpen(false));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`sac-transfer-${student.id}`}>
          <HeartHalf size={12} className="mr-1.5" /> {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Transfer {student.name} to Support a Child</DialogTitle></DialogHeader>
        <MissingFieldsBlock missing={missing} />
        <p className="text-sm text-[var(--vs-text-secondary)] mb-3">
          We pre-filled what we know from the student record. Add anything missing then submit. Required: full parent names, full address with pincode, photo, marksheet, 2+ activity reports, % obtained, favourite subject, special talent, achievement, what they want to be.
        </p>
        <SacProfileFormFields profile={profile} setField={setField} />
        <Button className="mt-4 bg-[var(--vs-secondary)] text-[var(--vs-bg)] w-full" disabled={busy} onClick={submit} data-testid="sac-transfer-submit">
          {busy ? "Submitting…" : "Submit to SAC portal"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default TransferToSacButton;
